import React from 'react';
import MetaTag from '@/components/ui/MetaTag';
import JsonLd from '@/components/ui/JsonLd';
import { BUSINESS } from '@/constants/business';
import { Breadcrumb, Button, Eyebrow, JournalShell, Section } from './components';
import { diamondCataloguePath } from './diamond-catalogue';
import type { DiamondCatalogueData } from './diamond-catalogue-server';
import { DiamondCatalogue, diamondCarat } from './diamond-catalogue-view';
import { SHAPES } from './diamond-config';
import type { UsdRate } from './forex';
import { MARKET_PAGES } from './market-pages';
import { PriceTabs } from './price-tabs';

export type DiamondCataloguePageProps = { catalogue: DiamondCatalogueData; usd: UsdRate | null };

/** Paginated reference configurations retain their own crawlable catalogue URL. */
export const DiamondCataloguePageLayout = ({ catalogue, usd }: DiamondCataloguePageProps) => {
  const path = diamondCataloguePath(catalogue.page);
  const url = `${BUSINESS.appUrl}${path}`;
  const inputs = catalogue.entries.map(({ profile }) => profile.input);
  const weights = Array.from(new Set(inputs.map(input => input.carat))).sort((left, right) => left - right);
  const weight = weights.length ? `${weights.map(diamondCarat).join(', ')} carat` : '';
  const shapes = Array.from(new Set(inputs.map(input => SHAPES.find(shape => shape.value === input.shape)?.name || input.shape))).join(', ');
  const colors = Array.from(new Set(inputs.map(input => input.color))).sort().join(', ');
  const clarities = Array.from(new Set(inputs.map(input => input.clarity))).join(', ');
  const title = `Giá kim cương ${weight} tham khảo – Trang ${catalogue.page}`;
  const description = `${catalogue.entries.length} cấu hình kim cương ${weight} tham khảo – Trang ${catalogue.page}. Dáng ${shapes}, màu ${colors}, độ tinh khiết ${clarities}. Xem giá và nguồn đối chiếu.`;
  const crumbs = [
    { label: 'Trang chủ', href: '/' },
    { label: 'Bảng giá', href: MARKET_PAGES.overview.path },
    { label: 'Kim cương', href: MARKET_PAGES.diamond.path },
    { label: `Trang ${catalogue.page}` },
  ];
  const latestQuoteDate = catalogue.entries.flatMap(({ estimate }) => estimate ? [estimate.updatedAt] : []).sort().pop();

  return <JournalShell activeKey="bang-gia">
    <MetaTag title={title} description={description} handle={path} image={`${BUSINESS.appUrl}/logo-bg.png`} />
    <JsonLd schema={[
      {
        '@context': 'https://schema.org', '@type': 'BreadcrumbList',
        itemListElement: crumbs.map((crumb, index) => ({
          '@type': 'ListItem', position: index + 1, name: crumb.label,
          item: `${BUSINESS.appUrl}${'href' in crumb && crumb.href ? crumb.href : path}`,
        })),
      },
      {
        '@context': 'https://schema.org', '@type': 'CollectionPage', '@id': `${url}#page`, url,
        name: title, description, inLanguage: 'vi', publisher: { '@id': BUSINESS.id },
        mainEntity: { '@id': `${url}#reference-list` },
        ...(latestQuoteDate ? { dateModified: latestQuoteDate } : {}),
      },
    ]} />
    <Breadcrumb items={crumbs} />
    <Section className="!tw-pt-6 md:!tw-pt-8 !tw-pb-6 md:!tw-pb-8">
      <Eyebrow>AN THƯ · BẢNG GIÁ KIM CƯƠNG</Eyebrow>
      <h1 className="tw-mt-3 tw-mb-0 tw-font-jr tw-text-[28px] md:tw-text-[44px] lg:tw-text-[48px] tw-font-normal tw-leading-[1.15]">{title}.</h1>
      <p className="tw-mt-3 tw-mb-0 tw-max-w-[820px] tw-text-[14px] md:tw-text-[17px] tw-leading-relaxed tw-text-jr-mute">Trang này gồm {catalogue.entries.length} cấu hình dáng {shapes}, màu {colors}, độ tinh khiết {clarities}. Mở từng mục để xem đầy đủ thông số, giá ước tính và liên kết nguồn.</p>
    </Section>
    <PriceTabs active="diamond" />
    <DiamondCatalogue data={catalogue} usd={usd} standalone />
    <Section className="!tw-pt-0 !tw-pb-10">
      <div className="tw-flex tw-flex-col sm:tw-flex-row sm:tw-items-center tw-justify-between tw-gap-5 tw-rounded-lg tw-bg-jr-band tw-p-5 md:tw-p-7">
        <div>
          <h2 className="tw-m-0 tw-font-jr tw-text-[24px] tw-font-normal">Bạn muốn xem thông số khác?</h2>
          <p className="tw-mt-2 tw-mb-0 tw-text-[14px] tw-leading-relaxed tw-text-jr-mute">Chọn carat, màu sắc và độ tinh khiết trong công cụ tra cứu.</p>
        </div>
        <Button href={MARKET_PAGES.diamond.path} className="sm:tw-shrink-0">Mở công cụ tra cứu</Button>
      </div>
    </Section>
  </JournalShell>;
};
